import { useRef, useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import {
  fieldId,
  SelectField,
  SuggestField,
  TextField,
  useForm,
  type FormErrors,
  type FormState,
} from '../../auth/CompanyFields';
import { t, tl, tx } from '../../i18n';
import { fmtDate } from '../../lib/format';
import { eurC, parseEuro } from '../../lib/money';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { ShareFile } from '../../components/ShareFile';
import { Preview } from '../belege/BelegSheet';
import type { EmployeeDetail } from './types';

// „Arbeitsvertrag erstellen“ in der Personalakte (Sam 12.10.): wie „Kündigung erstellen“ (Kuendigung.tsx). Der Mandant
// füllt das Formular aus (vorgefüllt aus dem Personalfragebogen) und bekommt den Mustervertrag der App als PDF
// (server/src/modules/lohn/arbeitsvertrag.ts) oder seine eigene Word-Vorlage mit eingesetzten Angaben. Die eigene
// Vorlage lädt er hier je Firma hoch. Das Formular ist übersetzt, der Vertrag bleibt Deutsch. Die Prüfungen hier
// entsprechen denen auf dem Server, damit die Meldung gleich am Feld steht. Gesetzes-Prüfung 13.10.: Tarifvertrag ja
// oder nein, Schichtsystem, Jugendliche, Lohngruppe und Branchenmindestlohn (Gebäudereinigung, Branche stellt die
// Kanzlei ein), Probezeit kürzer als die Befristung. Neue Felder erscheinen nur, wenn sie gebraucht werden.

interface Grenze {
  from: string;
  mindestlohn: number;
  minijob: number;
}
interface Vorlage {
  name: string;
  uploadedAt: string;
}
type Lohngruppe = 'innen' | 'glas';
interface BranchenLohn {
  from: string;
  to: string;
  lohn: Record<Lohngruppe, number>;
}
interface Vorschlag {
  today: string;
  azubi: boolean;
  grenzen: Grenze[];
  branche: string | null;
  branchenLoehne: BranchenLohn[];
  geburtsdatum: string | null;
  values: {
    art: string;
    beginn: string;
    befristetBis: string;
    probezeit: number;
    taetigkeit: string;
    arbeitsort: string;
    stunden: number | null;
    tage: number;
    verguetung: 'monat' | 'stunde';
    betrag: number | null;
    urlaub: number;
    tarif: '' | 'kein' | 'ja';
    tarifvertrag: string;
    strasse: string;
    plz: string;
    ort: string;
    unterzeichner: string;
    funktion: string;
    briefort: string;
  };
  vorlage: Vorlage | null;
  platzhalter: [string, string][];
}
interface Ergebnis {
  url: string;
  name: string;
  word: boolean;
  unbekannt: string[];
}

const ARTEN = [tl('Vollzeit'), tl('Teilzeit'), tl('Minijob'), tl('Werkstudent')] as const;
// Vorschläge; eigene Eingaben gehen auch (Namen der Tarifverträge bleiben Deutsch)
const TARIFE = [
  'Bundesrahmentarifvertrag für das Baugewerbe (BRTV) und Tarifvertrag über das Sozialkassenverfahren im Baugewerbe (VTV)',
  'Rahmentarifvertrag für die gewerblichen Beschäftigten in der Gebäudereinigung (RTV) und Tarifvertrag zur Regelung der Mindestlöhne in der Gebäudereinigung',
  'Manteltarifvertrag für das Wach- und Sicherheitsgewerbe',
];
const WOCHEN = 13 / 3;
const dateOk = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));
// „37,5“ oder „37.5“ → 37.5; leer oder ungültig → NaN
const num = (v: string) => (v.trim() ? Number(v.trim().replace(',', '.')) : NaN);
const zahl = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });
const str = (n: number | null) => (n === null ? '' : String(n).replace('.', ','));

// Alter in vollen Jahren am Tag day
function alterAm(geb: string, day: string): number {
  const [gy, gm, gd] = geb.split('-').map(Number) as [number, number, number];
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return y - gy - (m < gm || (m === gm && d < gd) ? 1 : 0);
}
// Mindesturlaub für Jugendliche nach dem Alter am 1. Januar (30, 27 oder 25 Werktage), auf Arbeitstage umgerechnet
function jugendUrlaub(geb: string, beginn: string, tage: number): number {
  const alter = alterAm(geb, `${beginn.slice(0, 4)}-01-01`);
  const werktage = alter < 16 ? 30 : alter < 17 ? 27 : 25;
  return Math.ceil((werktage * tage) / 6 - 1e-9);
}

function addMonths(s: string, n: number): string {
  const [y, m, d] = s.split('-').map(Number) as [number, number, number];
  const last = new Date(Date.UTC(y, m - 1 + n + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + n, Math.min(d, last))).toISOString().slice(0, 10);
}

export function VertragForm({ e, onDone, onCancel }: { e: EmployeeDetail; onDone(): void; onCancel(): void }) {
  const base = useApi<Vorschlag>(`/api/app/employee/${e.id}/arbeitsvertrag`);
  const v0 = base.data;
  if (!v0) return base.error ? <p className="meta">{base.error}</p> : <div className="spinner" aria-label={t('Lädt')} />;
  if (v0.azubi) {
    return (
      <div className="kdg">
        <p className="kdg-warn">
          {t(
            'Für Auszubildende gilt der Ausbildungsvertrag Ihrer Kammer (IHK oder Handwerkskammer). Den Vordruck gibt es dort, die Kammer trägt den Vertrag auch ein.',
          )}
        </p>
        <div className="row">
          <button className="btn2" type="button" onClick={onCancel}>
            {t('Zurück')}
          </button>
        </div>
      </div>
    );
  }
  return <Form e={e} v0={v0} onDone={onDone} onCancel={onCancel} />;
}

function Form({ e, v0, onDone, onCancel }: { e: EmployeeDetail; v0: Vorschlag; onDone(): void; onCancel(): void }) {
  const toast = useToast();
  const v = v0.values;
  const f = useForm({
    art: v.art,
    beginn: v.beginn,
    befristetBis: v.befristetBis,
    probezeit: String(v.probezeit),
    taetigkeit: v.taetigkeit,
    arbeitsort: v.arbeitsort,
    stunden: str(v.stunden),
    tage: String(v.tage),
    verguetung: v.verguetung,
    betrag: str(v.betrag),
    urlaub: str(v.urlaub),
    schichtsystem: '',
    lohngruppe: '',
    branchenlohnOk: '',
    erstuntersuchung: '',
    tarif: v.tarif,
    tarifvertrag: v.tarifvertrag,
    strasse: v.strasse,
    plz: v.plz,
    ort: v.ort,
    unterzeichner: v.unterzeichner,
    funktion: v.funktion,
    briefort: v.briefort,
  });
  const [vorlage, setVorlage] = useState(v0.vorlage);
  // Hat die Firma eine eigene Vorlage, ist sie vorgewählt
  const [welche, setWelche] = useState<'muster' | 'eigene'>(v0.vorlage ? 'eigene' : 'muster');
  const [wechselnd, setWechselnd] = useState(false);
  const [ueberstunden, setUeberstunden] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Ergebnis | null>(null);
  const val = (k: string) => (f.values[k] ?? '').trim();

  const beginn = val('beginn');
  const stunden = num(val('stunden'));
  const betrag = parseEuro(val('betrag'));
  const stunde = f.values.verguetung === 'stunde';
  // Mindestlohn ab heute bzw. ab Beginn, je nachdem, was später ist (wie auf dem Server)
  const stichtag = dateOk(beginn) && beginn > v0.today ? beginn : v0.today;
  const g = v0.grenzen.find((x) => stichtag >= x.from) ?? v0.grenzen[v0.grenzen.length - 1]!;
  const satz = typeof betrag === 'number' && stunden > 0 ? (stunde ? betrag : betrag / (stunden * WOCHEN)) : null;
  const monat = typeof betrag === 'number' && stunden > 0 ? (stunde ? betrag * stunden * WOCHEN : betrag) : null;
  // Branchenmindestlohn (Gebäudereinigung): der letzte bekannte Wert gilt weiter, bis ein neuer eingetragen ist
  const bl = v0.branchenLoehne.filter((x) => x.from <= stichtag).sort((a, b) => b.from.localeCompare(a.from))[0];
  const gruppe = f.values.lohngruppe as Lohngruppe | '';
  const blMin = bl && gruppe ? bl.lohn[gruppe] : null;
  const unterBranche = blMin !== null && satz !== null && satz >= g.mindestlohn - 0.005 && satz < blMin - 0.005;
  // Jugendliche: Alter am ersten Arbeitstag
  const alter = v0.geburtsdatum && dateOk(beginn) ? alterAm(v0.geburtsdatum, beginn) : null;
  const jugend = alter !== null && alter >= 15 && alter < 18;
  const tageN = num(val('tage'));
  const jugendMin = jugend ? jugendUrlaub(v0.geburtsdatum!, beginn, Math.min(5, Math.max(1, tageN || 5))) : 0;
  const ja = (k: string) => f.values[k] === 'ja';

  function check(): FormErrors {
    const errors: FormErrors = {};
    const bis = val('befristetBis');
    if (!dateOk(beginn)) errors.beginn = t('Bitte den Beginn angeben.');
    else if (beginn > addMonths(v0.today, 12)) errors.beginn = t('Bitte den Beginn prüfen.');
    if (alter !== null && alter < 15) {
      errors.beginn = t(
        'Am Beginn ist der Mitarbeiter noch keine 15 Jahre alt. Kinder dürfen in der Regel nicht arbeiten, deshalb erstellt die App keinen Vertrag.',
      );
    }
    if (bis && !dateOk(bis)) errors.befristetBis = t('Bitte das Ende der Befristung prüfen.');
    else if (bis && dateOk(beginn)) {
      if (bis <= beginn) errors.befristetBis = t('Das Ende der Befristung muss nach dem Beginn liegen.');
      else if (bis >= addMonths(beginn, 24)) {
        errors.befristetBis = t(
          'Ohne sachlichen Grund geht eine Befristung höchstens 2 Jahre (§ 14 Abs. 2 TzBfG). Für einen Sachgrund fragen Sie bitte einen Anwalt.',
        );
      } else if (Number(f.values.probezeit) > 0 && addMonths(beginn, Number(f.values.probezeit)) > bis) {
        errors.probezeit = t('Die Probezeit muss kürzer sein als die Befristung.');
      }
    }
    if (!val('taetigkeit')) errors.taetigkeit = t('Bitte die Tätigkeit angeben.');
    if (!val('arbeitsort')) errors.arbeitsort = t('Bitte den Arbeitsort angeben.');
    if (!(stunden >= 1 && stunden <= 48)) errors.stunden = t('Bitte die Wochenstunden prüfen (1 bis 48).');
    else if (f.values.art === 'Werkstudent' && stunden > 20) {
      errors.stunden = t('Werkstudenten arbeiten in der Vorlesungszeit höchstens 20 Stunden pro Woche.');
    } else if (jugend && stunden > 40) errors.stunden = t('Jugendliche unter 18 arbeiten höchstens 40 Stunden pro Woche.');
    const tage = tageN;
    const tageOk = Number.isInteger(tage) && tage >= 1 && tage <= 6;
    if (!tageOk) errors.tage = t('Bitte 1 bis 6 Arbeitstage angeben.');
    else if (jugend && tage > 5) errors.tage = t('Jugendliche unter 18 arbeiten höchstens 5 Tage pro Woche.');
    if (typeof betrag !== 'number' || betrag <= 0) errors.betrag = t('Bitte den Betrag angeben.');
    else if (satz !== null && satz < g.mindestlohn - 0.005) {
      errors.betrag = stunde
        ? t('Der Stundenlohn liegt unter dem gesetzlichen Mindestlohn von {min}.', { min: eurC(g.mindestlohn) })
        : t(
            'Bei {stunden} Stunden pro Woche sind das etwa {amount} pro Stunde, weniger als der gesetzliche Mindestlohn von {min}.',
            { stunden: zahl(stunden), amount: eurC(satz), min: eurC(g.mindestlohn) },
          );
    } else if (f.values.art === 'Minijob' && monat !== null && monat > g.minijob + 0.005) {
      errors.betrag = t(
        'Das sind etwa {amount} im Monat, mehr als die Minijob-Grenze von {max}. Bitte Stunden oder Lohn anpassen.',
        { amount: eurC(monat), max: eurC(g.minijob) },
      );
    }
    if (bl && !gruppe) errors.lohngruppe = t('Bitte die Lohngruppe wählen.');
    else if (unterBranche && !ja('branchenlohnOk')) errors.branchenlohnOk = t('Bitte prüfen und bestätigen.');
    if (ja('schicht') && !val('schichtsystem')) errors.schichtsystem = t('Bitte „Schichtsystem und Rhythmus“ ausfüllen.');
    const urlaub = num(val('urlaub'));
    if (!(urlaub >= 0 && urlaub <= 60)) errors.urlaub = t('Bitte die Urlaubstage prüfen.');
    else if (jugend && tageOk && tage <= 5 && urlaub < jugendMin) {
      errors.urlaub = t('Für Jugendliche sind mindestens {n} Urlaubstage vorgeschrieben (bei {tage} Arbeitstagen pro Woche).', {
        n: jugendMin,
        tage,
      });
    } else if (tageOk && urlaub < 4 * tage) {
      errors.urlaub = t(
        'Gesetzlich sind mindestens {n} Urlaubstage vorgeschrieben (bei {tage} Arbeitstagen pro Woche, § 3 BUrlG).',
        { n: 4 * tage, tage },
      );
    }
    if (jugend && !ja('erstuntersuchung')) {
      errors.erstuntersuchung = t('Jugendliche dürfen erst nach der ärztlichen Erstuntersuchung anfangen. Bitte bestätigen.');
    }
    if (!f.values.tarif) errors.tarif = t('Bitte angeben, ob ein Tarifvertrag gilt.');
    else if (f.values.tarif === 'ja' && !val('tarifvertrag')) errors.tarifvertrag = t('Bitte den Tarifvertrag angeben.');
    if (!val('unterzeichner')) errors.unterzeichner = t('Bitte angeben, wer unterschreibt.');
    if (!val('briefort')) errors.briefort = t('Bitte den Ort angeben.');
    return errors;
  }

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    if (welche === 'eigene' && !vorlage) {
      setError(t('Bitte zuerst Ihre Vorlage hochladen oder den Mustervertrag wählen.'));
      return;
    }
    const errors = check();
    const order = [
      'beginn',
      'befristetBis',
      'probezeit',
      'taetigkeit',
      'arbeitsort',
      'stunden',
      'tage',
      'betrag',
      'lohngruppe',
      'branchenlohnOk',
      'urlaub',
      'schichtsystem',
      'erstuntersuchung',
      'tarif',
      'tarifvertrag',
      'unterzeichner',
      'briefort',
    ];
    const first = order.find((k) => errors[k]);
    if (first) {
      f.setErrors(errors);
      document.getElementById(fieldId(first))?.focus();
      toast(errors[first]!);
      return;
    }
    setBusy(true);
    try {
      // Der Vertrag liegt danach kurz unter einer Adresse auf dem Server (ansehen, herunterladen, auch in der App)
      const r = await api.post<Ergebnis>(`/api/app/employee/${e.id}/arbeitsvertrag`, {
        vorlage: welche,
        art: f.values.art,
        beginn,
        befristetBis: val('befristetBis') || null,
        probezeit: Number(f.values.probezeit),
        taetigkeit: val('taetigkeit'),
        arbeitsort: val('arbeitsort'),
        wechselnd,
        stunden,
        tage: num(val('tage')),
        verguetung: f.values.verguetung,
        betrag,
        urlaub: num(val('urlaub')),
        ueberstunden,
        schicht: ja('schicht'),
        schichtsystem: ja('schicht') ? val('schichtsystem') : '',
        lohngruppe: bl ? gruppe || null : null,
        branchenlohnOk: unterBranche && ja('branchenlohnOk'),
        erstuntersuchung: jugend && ja('erstuntersuchung'),
        tarif: f.values.tarif,
        tarifvertrag: f.values.tarif === 'ja' ? val('tarifvertrag') : '',
        strasse: val('strasse'),
        plz: val('plz'),
        ort: val('ort'),
        unterzeichner: val('unterzeichner'),
        funktion: val('funktion'),
        briefort: val('briefort'),
      });
      setDone(r);
      document.querySelector('.mapp .sheet')?.scrollTo({ top: 0 });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    const bis = val('befristetBis');
    return (
      <div className="kdg">
        {done.word ? (
          <>
            <p className="kdg-ok" role="status">
              ✓ {t('Ihre Vorlage ist ausgefüllt.')}
            </p>
            {done.unbekannt.length > 0 && (
              <p className="kdg-warn">
                {t(
                  'Diese Platzhalter kennt die App nicht, sie stehen noch so in der Datei: {list}. Bitte in Word von Hand ausfüllen.',
                  {
                    list: done.unbekannt.map((k) => `{${k}}`).join(', '),
                  },
                )}
              </p>
            )}
          </>
        ) : (
          <Preview url={done.url} mime="application/pdf" name={done.name} />
        )}
        <div className="row">
          <a className="btn2 dark" href={`${done.url}?download=1`} download={done.name}>
            {done.word ? t('Word-Datei herunterladen') : t('PDF herunterladen')}
          </a>
          <ShareFile url={`${done.url}?download=1`} name={done.name} />
          <button className="btn2" type="button" onClick={() => setDone(null)}>
            {t('Ändern')}
          </button>
        </div>
        <ol className="kdg-steps">
          {done.word && <li>{t('In Word öffnen und alles prüfen.')}</li>}
          <li>{t('Zweimal ausdrucken.')}</li>
          <li>{t('Beide Exemplare von Hand unterschreiben lassen, von Ihnen und vom Mitarbeiter. Jeder behält eins.')}</li>
          {jugend && <li>{t('Weil der Mitarbeiter noch nicht 18 ist, unterschreiben auch die Eltern.')}</li>}
          {bis && (
            <li>
              {t('Wegen der Befristung muss der Vertrag vor dem ersten Arbeitstag ({date}) unterschrieben sein.', {
                date: fmtDate(beginn),
              })}
            </li>
          )}
        </ol>
        <div className="row">
          <button className="btn2" type="button" onClick={onDone}>
            {t('Fertig')}
          </button>
        </div>
      </div>
    );
  }

  const choice = (value: 'muster' | 'eigene', title: string, text: string) => (
    <label className={welche === value ? 'on' : undefined}>
      <input type="radio" name="vorlage" checked={welche === value} onChange={() => setWelche(value)} />
      <span>
        <b>{title}</b>
        <small>{text}</small>
      </span>
    </label>
  );

  return (
    <form className="form maform mform kdg" onSubmit={submit} noValidate>
      <fieldset className="who">
        <legend>{t('Welcher Vertrag?')}</legend>
        {choice('muster', t('Mustervertrag der App'), t('Fertiger Vertrag nach dem Nachweisgesetz, als PDF zum Ausdrucken.'))}
        {choice(
          'eigene',
          t('Eigene Vorlage'),
          vorlage
            ? t('{name} · Die App setzt die Angaben ein, Sie bekommen eine Word-Datei.', { name: vorlage.name })
            : t('Ihren eigenen Vertrag als Word-Datei hochladen. Die App setzt die Angaben ein.'),
        )}
      </fieldset>
      {welche === 'eigene' && (
        <VorlageBox clientId={e.clientId} vorlage={vorlage} platzhalter={v0.platzhalter} onChange={setVorlage} />
      )}

      <fieldset>
        <legend>{t('Beschäftigung')}</legend>
        <div className="two">
          <SelectField f={f} name="art" label={t('Beschäftigungsart')} required options={ARTEN} />
          <TextField f={f} name="beginn" label={t('Beginn')} required type="date" max={addMonths(v0.today, 12)} />
        </div>
        <div className="two">
          <TextField
            f={f}
            name="befristetBis"
            label={t('Befristet bis (leer = unbefristet)')}
            type="date"
            min={beginn || undefined}
          />
          <Choice
            f={f}
            name="probezeit"
            label={t('Probezeit')}
            options={[0, 1, 2, 3, 4, 5, 6].map((n) => [
              String(n),
              n === 0 ? t('keine') : n === 1 ? t('1 Monat') : t('{n} Monate', { n }),
            ])}
          />
        </div>
        {val('befristetBis') && (
          <small className="fh">
            {t(
              'Befristung ohne sachlichen Grund: höchstens 2 Jahre und nur, wenn der Mitarbeiter nicht schon früher bei Ihnen angestellt war (§ 14 Abs. 2 TzBfG).',
            )}
          </small>
        )}
        <TextField
          f={f}
          name="taetigkeit"
          label={t('Tätigkeit')}
          required
          maxLength={120}
          placeholder={t('z. B. Maurer, Reinigungskraft')}
        />
        <TextField
          f={f}
          name="arbeitsort"
          label={wechselnd ? t('Sitz des Betriebs') : t('Arbeitsort')}
          required
          maxLength={160}
        />
        <label className="chk">
          <input type="checkbox" checked={wechselnd} onChange={(ev) => setWechselnd(ev.target.checked)} />
          <span>{t('Wechselnde Einsatzorte (z. B. Baustellen oder Objekte von Kunden)')}</span>
        </label>
      </fieldset>

      <fieldset>
        <legend>{t('Arbeitszeit, Lohn und Urlaub')}</legend>
        <div className="two">
          <TextField f={f} name="stunden" label={t('Wöchentliche Arbeitsstunden')} required inputMode="decimal" maxLength={6} />
          <TextField f={f} name="tage" label={t('Arbeitstage pro Woche')} required inputMode="numeric" maxLength={1} />
        </div>
        <div className="two">
          <Choice
            f={f}
            name="verguetung"
            label={t('Vergütung')}
            options={[
              ['monat', t('Monatsgehalt')],
              ['stunde', t('Stundenlohn')],
            ]}
          />
          <TextField
            f={f}
            name="betrag"
            label={stunde ? t('Stundenlohn brutto (€)') : t('Monatsgehalt brutto (€)')}
            required
            inputMode="decimal"
            maxLength={12}
          />
        </div>
        <small className="fh">
          {satz !== null && !stunde
            ? t('Etwa {amount} pro Stunde. Gesetzlicher Mindestlohn: {min}.', { amount: eurC(satz), min: eurC(g.mindestlohn) })
            : t('Gesetzlicher Mindestlohn: {min} pro Stunde.', { min: eurC(g.mindestlohn) })}
          {f.values.art === 'Minijob' && ` ${t('Minijob: höchstens {max} im Monat.', { max: eurC(g.minijob) })}`}
        </small>
        {bl && (
          <>
            <Choice
              f={f}
              name="lohngruppe"
              label={t('Lohngruppe (Gebäudereinigung)')}
              required
              options={[
                ['', t('Bitte wählen')],
                ['innen', t('Innen- und Unterhaltsreinigung (mindestens {min})', { min: eurC(bl.lohn.innen) })],
                ['glas', t('Glas- und Fassadenreinigung (mindestens {min})', { min: eurC(bl.lohn.glas) })],
              ]}
            />
            {bl.to < stichtag && (
              <small className="fh">
                {t('Der Branchenmindestlohn galt bis {date}. Bitte fragen Sie Ihre Kanzlei nach dem neuen Wert.', {
                  date: fmtDate(bl.to),
                })}
              </small>
            )}
            {unterBranche && (
              <>
                <p className="kdg-warn">
                  {t('Der Lohn liegt unter dem Mindestlohn der Gebäudereinigung von {min} pro Stunde.', { min: eurC(blMin!) })}
                </p>
                <Check f={f} name="branchenlohnOk" label={t('Geprüft, der Lohn stimmt so')} required />
              </>
            )}
          </>
        )}
        <TextField f={f} name="urlaub" label={t('Urlaubstage pro Jahr')} required inputMode="decimal" maxLength={5} />
        {jugend && (
          <>
            <p className="kdg-warn">
              {t(
                'Der Mitarbeiter ist am Beginn {alter} Jahre alt. Für Jugendliche gelten höchstens 40 Stunden an 5 Tagen pro Woche und mindestens {n} Urlaubstage.',
                { alter: alter!, n: jugendMin },
              )}
            </p>
            <Check f={f} name="erstuntersuchung" label={t('Die ärztliche Erstuntersuchung liegt vor.')} required />
          </>
        )}
        <label className="chk">
          <input type="checkbox" checked={ueberstunden} onChange={(ev) => setUeberstunden(ev.target.checked)} />
          <span>{t('Überstunden bei Bedarf (werden bezahlt oder mit Freizeit ausgeglichen)')}</span>
        </label>
        <Check f={f} name="schicht" label={t('Schichtdienst')} />
        {ja('schicht') && (
          <TextField
            f={f}
            name="schichtsystem"
            label={t('Schichtsystem und Rhythmus')}
            required
            maxLength={200}
            placeholder={t('z. B. Früh- und Spätschicht im Wochenwechsel')}
          />
        )}
      </fieldset>

      <fieldset>
        <legend>{t('Tarifvertrag')}</legend>
        <Choice
          f={f}
          name="tarif"
          label={t('Gilt ein Tarifvertrag?')}
          required
          options={[
            ['', t('Bitte wählen')],
            ['kein', t('Nein, keiner (auch kein allgemeinverbindlicher)')],
            ['ja', t('Ja')],
          ]}
        />
        {f.values.tarif === 'ja' && (
          <SuggestField f={f} name="tarifvertrag" label={t('Welcher Tarifvertrag?')} required options={TARIFE} maxLength={300} />
        )}
        <small className="fh">
          {t(
            'In Bau und Gebäudereinigung gelten allgemeinverbindliche Tarifverträge für alle Betriebe der Branche, auch ohne Mitgliedschaft im Verband. Fragen Sie im Zweifel Ihre Kanzlei.',
          )}
        </small>
      </fieldset>

      <fieldset>
        <legend>{t('Anschrift von {name}', { name: e.name })}</legend>
        <TextField f={f} name="strasse" label={t('Straße und Hausnummer')} maxLength={120} autoComplete="off" />
        <div className="two">
          <TextField f={f} name="plz" label={t('PLZ')} maxLength={10} inputMode="numeric" autoComplete="off" />
          <TextField f={f} name="ort" label={t('Ort')} maxLength={80} autoComplete="off" />
        </div>
      </fieldset>
      <fieldset>
        <legend>{t('Unterschrift')}</legend>
        <div className="two">
          <TextField f={f} name="unterzeichner" label={t('Unterschrift von')} required maxLength={100} autoComplete="name" />
          <TextField f={f} name="funktion" label={t('Funktion')} maxLength={60} placeholder={t('z. B. Geschäftsführung')} />
        </div>
        <TextField f={f} name="briefort" label={t('Ort des Schreibens')} required maxLength={80} />
      </fieldset>
      {error && (
        <p className="formerr" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button className="btn2 dark" type="submit" disabled={busy}>
          {busy ? t('Einen Moment …') : welche === 'eigene' ? t('Vorlage ausfüllen (Word)') : t('Arbeitsvertrag erstellen (PDF)')}
        </button>
        <button className="btn2" type="button" onClick={onCancel}>
          {t('Abbrechen')}
        </button>
      </div>
      <div className="src">
        {t(
          'Gültig wird der Vertrag mit der Unterschrift von Hand auf Papier, von Ihnen und vom Mitarbeiter. In Bau, Reinigung, Sicherheit, Transport, Gastronomie und einigen weiteren Branchen ist das ohnehin Pflicht. Eine Befristung muss vor dem ersten Arbeitstag unterschrieben sein, sonst gilt der Vertrag unbefristet. Der Mustervertrag ersetzt keine Rechtsberatung.',
        )}
      </div>
    </form>
  );
}

// Auswahlfeld mit eigenen Werten (Wert für die API, Text schon übersetzt)
function Choice({
  f,
  name,
  label,
  options,
  required,
}: {
  f: FormState;
  name: string;
  label: string;
  options: [string, string][];
  required?: boolean;
}) {
  const id = fieldId(name);
  const err = f.errors[name];
  return (
    <div className="fld">
      <label htmlFor={id}>
        <span>
          {label}
          {required && (
            <>
              {' '}
              <span className="req" aria-hidden="true">
                *
              </span>
            </>
          )}
        </span>
        <select
          id={id}
          name={name}
          value={f.values[name] ?? ''}
          onChange={(ev) => f.set(name, ev.target.value)}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${id}-err` : undefined}
        >
          {options.map(([value, text]) => (
            <option key={value} value={value} disabled={value === '' && required}>
              {text}
            </option>
          ))}
        </select>
      </label>
      {err && (
        <p className="ferr" id={`${id}-err`}>
          {err}
        </p>
      )}
    </div>
  );
}

// Häkchen im Formular (Wert „ja“), mit Meldung darunter
function Check({ f, name, label, required }: { f: FormState; name: string; label: string; required?: boolean }) {
  const id = fieldId(name);
  const err = f.errors[name];
  return (
    <div className="fld">
      <label className="chk" htmlFor={id}>
        <input
          type="checkbox"
          id={id}
          checked={f.values[name] === 'ja'}
          onChange={(ev) => f.set(name, ev.target.checked ? 'ja' : '')}
          aria-invalid={err ? true : undefined}
          aria-describedby={err ? `${id}-err` : undefined}
        />
        <span>
          {label}
          {required && (
            <>
              {' '}
              <span className="req" aria-hidden="true">
                *
              </span>
            </>
          )}
        </span>
      </label>
      {err && (
        <p className="ferr" id={`${id}-err`}>
          {err}
        </p>
      )}
    </div>
  );
}

// Eigene Vorlage der Firma: hochladen, ansehen, ersetzen, entfernen; dazu die Liste der Platzhalter
function VorlageBox({
  clientId,
  vorlage,
  platzhalter,
  onChange,
}: {
  clientId: string;
  vorlage: Vorlage | null;
  platzhalter: [string, string][];
  onChange(v: Vorlage | null): void;
}) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const url = `/api/app/lohn/${clientId}/vertragsvorlage`;

  async function upload(file: File | undefined) {
    if (!file) return;
    setError(null);
    if (!/\.docx$/i.test(file.name)) {
      setError(t('Bitte eine Word-Datei (.docx) hochladen.'));
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('vorlage', file, file.name);
      onChange(await api.post<Vorlage>(url, fd));
      toast(t('Vorlage gespeichert'));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setError(null);
    setBusy(true);
    try {
      await api.del(url);
      onChange(null);
      toast(t('Vorlage entfernt'));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="vl-box">
      {vorlage ? (
        <p className="vl-file">
          ✓ {vorlage.name}
          <small>{t('hochgeladen am {date}', { date: fmtDate(vorlage.uploadedAt) })}</small>
        </p>
      ) : (
        <p className="vl-file">{t('Noch keine eigene Vorlage hochgeladen.')}</p>
      )}
      <div className="row">
        {vorlage && (
          <a className="btn2" href={url} download={vorlage.name}>
            {t('Herunterladen')}
          </a>
        )}
        <button type="button" className={vorlage ? 'btn2' : 'btn2 dark'} disabled={busy} onClick={() => input.current?.click()}>
          {busy ? t('Einen Moment …') : vorlage ? t('Ersetzen') : t('Word-Datei hochladen')}
        </button>
        {vorlage && (
          <button type="button" className="btn2" disabled={busy} onClick={remove}>
            {t('Entfernen')}
          </button>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        hidden
        data-testid="vl-file"
        onChange={(ev) => {
          void upload(ev.target.files?.[0]);
          ev.target.value = '';
        }}
      />
      {error && (
        <p className="ferr" role="alert">
          {error}
        </p>
      )}
      <details className="vl-help">
        <summary>{t('So bereiten Sie Ihre Vorlage vor')}</summary>
        <p>
          {t(
            'Schreiben Sie in Ihren Vertrag dort, wo Angaben hin sollen, einen Platzhalter in geschweiften Klammern, z. B. {Vorname} oder {Beginn}. Groß- und Kleinschreibung ist egal. Dann als Word-Datei (.docx) speichern und hier hochladen. Die Vorlage gilt für alle Mitarbeiter dieser Firma.',
          )}
        </p>
        <dl>
          {platzhalter.map(([name, text]) => (
            <div key={name}>
              <dt>{`{${name}}`}</dt>
              <dd>{tx(text)}</dd>
            </div>
          ))}
        </dl>
      </details>
    </div>
  );
}
